import { Box, Button, TextField, InputAdornment } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import FieldVisitTable from "page-sections/FieldVisit/FieldVisitTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { Search } from "@mui/icons-material";

const AssignedFieldVisit = () => {
  const { setPageTitle } = usePageTitle();
  const [viewMode, setViewMode] = useState("list");
  const [fieldVisits, setFieldVisits] = useState([]);
  const [calendarVisits, setCalendarVisits] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  useEffect(() => {
    setPageTitle("Assign Field Visit");
  }, [setPageTitle]);

  const fetchFieldVisits = useCallback(async (page = 1, pageSizeParam = 10, search = "") => {
    setLoading(true);
    try {
      let url = API_ENDPOINTS.GET_assigned_field_visit;
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSizeParam));
      if (search && search.trim()) {
        params.set("search", search.trim());
      }
      url += (url.includes("?") ? "&" : "?") + params.toString();

      const response = await apiService.get(url);
      const list =
        response?.data?.AssignedFieldVisit ||
        response?.data?.assigned_field_visit ||
        response?.data?.FieldVisit ||
        (Array.isArray(response?.data) ? response.data : []);

      if (response?.success && Array.isArray(list)) {
        setFieldVisits(list);
      } else {
        setFieldVisits([]);
      }

      const paginationData = response?.pagination || response?.data?.pagination || {};
      if (Object.keys(paginationData).length > 0) {
        setPagination({
          current_page: paginationData.current_page ?? page,
          page_size: paginationData.page_size ?? pageSizeParam,
          total_items: paginationData.total_items ?? 0,
          total_pages: paginationData.total_pages ?? 1,
          has_next: paginationData.has_next ?? false,
          has_previous: paginationData.has_previous ?? false,
          next_page_number: paginationData.next_page_number ?? null,
          previous_page_number: paginationData.previous_page_number ?? null,
        });
        setCurrentPage(paginationData.current_page ?? page);
        setPageSize(paginationData.page_size ?? pageSizeParam);
      } else {
        setPagination((prev) => ({
          ...prev,
          total_items: list.length,
          total_pages: 1,
          current_page: 1,
          page_size: pageSizeParam,
        }));
      }
    } catch (error) {
      console.error("API error:", error);
      setFieldVisits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalendarVisits = useCallback(async () => {
    setCalendarLoading(true);
    try {
      let url = API_ENDPOINTS.GET_assigned_field_visit;
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("page_size", "500");
      url += (url.includes("?") ? "&" : "?") + params.toString();

      const response = await apiService.get(url);
      const list =
        response?.data?.AssignedFieldVisit ||
        response?.data?.assigned_field_visit ||
        response?.data?.FieldVisit ||
        (Array.isArray(response?.data) ? response.data : []);

      setCalendarVisits(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Calendar API error:", error);
      setCalendarVisits([]);
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  const handleStatusUpdate = async (id, visitStatus, remarks, visitDate) => {
    try {
      const payload = {
        id,
        visit_status: visitStatus,
      };
      if (remarks !== undefined) {
        payload.remarks = remarks;
      }
      if (visitDate) {
        payload.visit_date = visitDate;
      }
      await apiService.put(API_ENDPOINTS.PUT_update_assigned_field_visit_status, payload);
      fetchFieldVisits(currentPage, pageSize, searchTerm);
      fetchCalendarVisits();
    } catch (error) {
      console.error("Error updating field visit status:", error);
    }
  };

  const handleAssignFieldVisit = async (payload) => {
    try {
      await apiService.post(API_ENDPOINTS.POST_assign_field_visit, payload);
      fetchFieldVisits(currentPage, pageSize, searchTerm);
      fetchCalendarVisits();
    } catch (error) {
      console.error("Error assigning field visit:", error);
      throw error;
    }
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    fetchFieldVisits(value, pageSize, searchTerm);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchFieldVisits(1, newSize, searchTerm);
  };

  const handleSearch = () => {
    if (!searchValue.trim()) return;
    setSearchTerm(searchValue);
    setCurrentPage(1);
    setHasSearched(true);
    fetchFieldVisits(1, pageSize, searchValue);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setSearchTerm("");
    setCurrentPage(1);
    setHasSearched(false);
    fetchFieldVisits(1, pageSize, "");
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && searchValue.trim()) {
      handleSearch();
    }
  };

  useEffect(() => {
    if (searchValue === "" && searchTerm !== "") {
      handleClearSearch();
    }
  }, [searchValue]);

  useEffect(() => {
    fetchFieldVisits(currentPage, pageSize, searchTerm);
    fetchCalendarVisits();
  }, [fetchFieldVisits, fetchCalendarVisits]);

  return (
    <Box pt={2} pb={4}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          backgroundColor: "background.default",
          pt: 0.5,
          pb: 1,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "12px",
            p: "12px 16px",
            mb: 2,
            bgcolor: "background.paper",
            borderRadius: "10px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: { xs: "1 1 100%", lg: "1 1 0" },
              minWidth: { xs: "100%", lg: 280 },
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search by farmer name, contact, village etc"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                if (hasSearched && e.target.value !== searchTerm) {
                  setHasSearched(false);
                }
              }}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "8px",
                  fontSize: 14,
                  pl: 0.5,
                  "& fieldset": { borderColor: "#d1d5db" },
                  "&:hover fieldset": { borderColor: "#9ca3af" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                  },
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={handleSearch}
              disabled={!searchValue.trim() || (searchValue === searchTerm && hasSearched)}
              sx={{
                height: 40,
                minWidth: 90,
                borderRadius: 2,
                fontSize: 14,
                fontWeight: 500,
                textTransform: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                px: 2,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Search
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 1 }}>
        <FieldVisitTable
          fieldVisits={fieldVisits}
          searchQuery={searchTerm}
          loading={loading}
          onRefresh={() => {
            fetchFieldVisits(currentPage, pageSize, searchTerm);
            fetchCalendarVisits();
          }}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={pageSizeOptions}
          assignedFieldVisitMode
          onStatusUpdate={handleStatusUpdate}
          onAssign={handleAssignFieldVisit}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          calendarVisits={calendarVisits}
          calendarLoading={calendarLoading}
        />
      </Box>
    </Box>
  );
};

export default AssignedFieldVisit;
